import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Select } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import type { Seo } from '@sravni/types/lib/seo';

import type { IFilters } from '@src/@types/microcredits';
import { AuthSwitch } from '@src/components/AuthSwitch';
import { AnchorLinkList } from '@src/components/PageHeader/AnchorLinkList';
import { DESKTOP_BUTTON_SIZE, MOBILE_BUTTON_SIZE } from '@src/components/PageHeader/consts';
import { MobilePageHeadingB } from '@src/components/PageHeader/MobilePageHeadingB';
import { AmountControlV2 } from '@src/components/ui/AmountControlV2';
import { PRODUCT_LIST_ANCHOR } from '@src/constants/common';
import { FILTERS_NAMES } from '@src/constants/filters';
import {
    sendChangeObtainingMethodFilterEvent,
    sendChangeTermFilterEventB,
    sendUserWantFreeLoanEvent,
} from '@src/helpers/analyticsEvents';
import { scrollToElementById } from '@src/helpers/common';
import { openAuthDialog } from '@src/reducers/authDialog';
import { useHasUtmLabel } from '@src/reducers/route';
import { useUser } from '@src/reducers/user';
import { isNaKartuPage } from '@src/utils/routing';

import type { ObtainingMethod } from './consts';
import { LoanTerm, OBTAINING_METHOD_OPTIONS, TERM_FILTER_VALUES, TERM_OPTIONS } from './consts';
import styles from './styles.module.scss';

type Props = {
    className?: string;
    difference?: number;
    filters: IFilters;
    conditionsActive?: boolean;
    onChange: (value: any, name: string) => void;
    onToggleConditionDialog?: () => void;
    seo?: Partial<Seo>;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

export const PageHeaderFiltersB = memo(({ filters, onChange, seo }: Props) => {
    const isMobile = useIsMobile();
    const user = useUser();
    const { asPath } = useRouter();
    const dispatch = useDispatch();
    const hasUtmLabel = useHasUtmLabel();
    const [pathWithoutQuery] = asPath.split('?');
    const showAuthSwitch = !user && isNaKartuPage(pathWithoutQuery);

    const handleClickSelectionButton = useCallback(() => {
        scrollToElementById(PRODUCT_LIST_ANCHOR);
    }, []);

    const handleChangeObtainingMethodFilter = useCallback(
        (value: ObtainingMethod) => {
            sendChangeObtainingMethodFilterEvent(
                OBTAINING_METHOD_OPTIONS.find((el) => el.value === value)?.label ?? '',
            );

            return onChange(value, FILTERS_NAMES.OBTAINING_METHOD);
        },
        [onChange],
    );

    const handleTermChange = useCallback(
        (value: LoanTerm) => {
            const optionFilters = value ? TERM_FILTER_VALUES[value] : TERM_FILTER_VALUES[LoanTerm.ANY];

            sendChangeTermFilterEventB(TERM_OPTIONS.find((el) => el.value === value)?.label ?? '');

            Object.entries(optionFilters).forEach(([key, filterValue]) => onChange(filterValue, key));
        },
        [onChange],
    );

    const getTermLabel = useCallback((options: Array<{ label: string; value: LoanTerm }>, filterValue: number) => {
        if (!filterValue) return TERM_OPTIONS[0].value;

        const appropriateOption = options.find((option) => {
            const termRangeTo = TERM_FILTER_VALUES[option.value]?.termRangeTo;

            if (typeof termRangeTo === 'number') {
                return filterValue <= termRangeTo;
            }
            return false;
        });

        return appropriateOption?.value;
    }, []);

    const onSwitchClick = useCallback(() => {
        sendUserWantFreeLoanEvent();
        dispatch(openAuthDialog({ dialogInitialType: 'firstFreeLoan' }));
    }, [dispatch]);

    return (
        <>
            {isMobile && !hasUtmLabel && <MobilePageHeadingB heading={seo.heading} subHeading={seo.subHeading} />}

            <div className={styles.wrapper}>
                <div className={styles.filters}>
                    <div className={styles.sumField}>
                        <AmountControlV2
                            name={FILTERS_NAMES.AMOUNT}
                            className={styles.amount}
                            label="Мне нужно"
                            value={filters[FILTERS_NAMES.AMOUNT]}
                            max={100000}
                            onChange={onChange}
                            postfix="₽"
                            steps={[
                                { ceilThreshold: 30000, step: 1000 },
                                { ceilThreshold: 100000, step: 5000 },
                            ]}
                        />
                    </div>
                    <div className={styles.sumField}>
                        <Select
                            label="На срок"
                            value={getTermLabel(TERM_OPTIONS, filters[FILTERS_NAMES.TERM_RANGE_TO])}
                            options={TERM_OPTIONS}
                            onChange={handleTermChange}
                            optionsSort={false}
                        />
                    </div>
                    <div className={styles.sumField}>
                        <Select
                            label="Способ получения"
                            value={filters[FILTERS_NAMES.OBTAINING_METHOD] ?? OBTAINING_METHOD_OPTIONS[0].value}
                            options={OBTAINING_METHOD_OPTIONS}
                            onChange={handleChangeObtainingMethodFilter}
                            optionsSort={false}
                        />
                    </div>
                    {showAuthSwitch && isMobile && <AuthSwitch onClick={onSwitchClick} />}

                    <Button
                        variant="primary"
                        size={isMobile ? MOBILE_BUTTON_SIZE : DESKTOP_BUTTON_SIZE}
                        onClick={handleClickSelectionButton}
                        className={styles.button}
                    >
                        Показать предложения
                    </Button>
                </div>
                {showAuthSwitch && !isMobile && <AuthSwitch onClick={onSwitchClick} />}
                <AnchorLinkList />
            </div>
        </>
    );
});
